import { ExpressionPart, ExpressionPartTypes } from 'src/models/calculations/CalculationDetailsResult';
import WorksheetTotal from 'src/models/tp-allocation/WorksheetTotal';

const flatten = (parts: ExpressionPart[]): ExpressionPart[] =>
    parts.reduce((all, part) => all.concat(part, part.subTokens ? flatten(part.subTokens) : []), [] as ExpressionPart[]);

const nameOf = (total: WorksheetTotal) => (total.description || '').trim();

/** True when `a` and `b` are the same total: the same object, or copies with the same saved id. */
const isSameTotal = (a: WorksheetTotal, b: WorksheetTotal) =>
    a === b || (a.totalId != null && b.totalId != null && a.totalId === b.totalId);

/** Other totals whose formula refers to `name`. */
export function totalsReferencing(name: string, totals: WorksheetTotal[], self?: WorksheetTotal): WorksheetTotal[] {
    if (!name) {
        return [];
    }
    return totals.filter((t) => !(self && isSameTotal(t, self)) && (t.expression || '').includes(`[${name}]`));
}

/**
 * Checks run before a total is sent. `parts` are the formula tokens the save will send. A total may
 * reference Step 1 descriptions and the totals above it only, which rules out forward references and
 * cycles. Returns the first problem, or undefined.
 */
export function validateDasTotal(total: WorksheetTotal, totals: WorksheetTotal[], dataSourceNames: string[],
                                 parts: ExpressionPart[]): string | undefined {
    const name = nameOf(total);
    if (!name) {
        return `Total #${total.visibleSequence ?? total.sequence} needs a name.`;
    }
    if (totals.some((t) => !isSameTotal(t, total) && nameOf(t) === name)) {
        return 'Total names must be unique.';
    }
    if (dataSourceNames.some((d) => (d || '').trim() === name)) {
        return `"${name}" has the same name as a data source.`;
    }
    if (!(total.expression || '').trim()) {
        return `"${name}" needs a formula.`;
    }
    const tokens = flatten(parts);
    const unknown = tokens.filter((p) => p.type === ExpressionPartTypes.PARAMETER || p.type === ExpressionPartTypes.UNKNOWN);
    if (unknown.length > 0) {
        return `"${name}" references unknown item(s): ${unknown.map((p) => p.value || '[]').join(', ')}.`;
    }
    const byReference = totals.indexOf(total);
    const index = byReference >= 0 ? byReference : totals.findIndex((t) => isSameTotal(t, total));
    const position = index >= 0 ? index : totals.length;
    const stepNames = tokens.filter((p) => p.type === ExpressionPartTypes.STEP).map((p) => p.value.trim());
    if (stepNames.includes(name)) {
        return `"${name}" cannot reference itself.`;
    }
    const below = new Set(totals.slice(position + 1).map(nameOf));
    if (stepNames.some((s) => below.has(s))) {
        return `"${name}" can only reference totals above it.`;
    }
    return undefined;
}
